/**
 * ClassificationAnalysis â€” Segment-wise performance deep dive.
 *
 * Filter flow (step-by-step):
 *   1. Class (10 / 12)
 *   2. Year
 *   3. View (Graphical / Tabular)
 *   4. Level (Region / State / District / KV)  â€” default: Region
 *   5. Classification (Overall / Gender / Category / â€¦)  â€” default: Overall
 *   6. Metric Mode (Pass Stats / PI / Overall)
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ChevronRight, Network, Map, Layers, School, BookOpen, TrendingUp, GitCompare } from 'lucide-react';
import {
  useGetResultRegionDetailsQuery,
  useGetResultOverallRegionDetailsQuery,
  useGetResultOverallAllKVsQuery,
  useGetResultAllKVsDetailsQuery,
  useGetResultOverallStateDetailsQuery,
  useGetResultStateDetailsQuery,
  useGetResultDistrictDetailsQuery,
  useGetResultOverallDistrictDetailsQuery,
  useGetResultYearValueDropdownQuery,
  useGetResultStreamWiseQuery,
} from '../services/api';

import type { GradeTab, MetricMode, ViewMode, AnalysisLevel, ClassGroup, EntityRow, SegmentStats, ComparisonItem } from '../components/classification/types';
import { GRADE_TABS, ANALYSIS_LEVELS, METRIC_MODES, BASE_GROUPS, STREAM_GROUP, STREAM_SEGMENT_BY_DB_VALUE } from '../components/classification/constants';
import Chip from '../components/classification/Chip';
import ViewModeToggle from '../components/classification/ViewModeToggle';
import LevelBreadcrumb from '../components/classification/LevelBreadcrumb';
import SectionLabel from '../components/classification/SectionLabel';
import DivergingChart from '../components/classification/DivergingChart';
import RankingPanel from '../components/classification/RankingPanel';
import EntityBarChart from '../components/classification/EntityBarChart';
import SegmentComparisonChart from '../components/classification/SegmentComparisonChart';
import SummaryCards from '../components/classification/SummaryCards';
import AggSummaryCards from '../components/classification/AggSummaryCards';
import AggTable from '../components/classification/AggTable';
import KVSLoader from '../../../components/loaders/KVSLoader';
import KVTable from '../components/classification/KVTable';

// Classification groups supported at district level
const DISTRICT_SUPPORTED_GROUPS: ClassGroup[] = ['overall', 'gender', 'socialcategory', 'cwsn'];

export default function ClassificationAnalysis() {
  const navigate = useNavigate();

  // Step 1: Class
  const [activeTab, setActiveTab] = useState<GradeTab>('class10');
  // Step 2: Year
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  // Step 3: View
  const [viewMode, setViewMode] = useState<ViewMode>('graphical');
  // Step 4: Level
  const [analysisLevel, setAnalysisLevel] = useState<AnalysisLevel>('RO');
  // Step 5: Classification
  const [selectedGroup, setSelectedGroup] = useState<ClassGroup>('overall');
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  // Step 6: Metric
  const [metricMode, setMetricMode] = useState<MetricMode>('overall');

  // Drill-down selections
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [selectedKv, setSelectedKv] = useState<string | null>(null);

  const grade = activeTab === 'class10' ? 10 : 12;

  const { data: yearDropdownData } = useGetResultYearValueDropdownQuery({ grade });
  const yearOptions = useMemo(function () {
    const nums = (yearDropdownData || []).map(Number).filter(Number.isFinite);
    return nums.length > 0 ? nums : [selectedYear];
  }, [yearDropdownData, selectedYear]);

  // Auto-select first valid year when options load
  React.useEffect(function () {
    if (yearOptions.length > 0 && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[0]);
    }
  }, [yearOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Which streams ran nationally in the selected session. Vocational exists in
  // some sessions only, so the segment is offered just where it has data.
  const { data: streamWiseData } = useGetResultStreamWiseQuery(
    { grade: grade as 10 | 12, year: selectedYear },
    { skip: grade !== 12 },
  );

  const streamGroup = useMemo(function () {
    const rows = streamWiseData ?? [];
    if (!rows.length) {
      return { ...STREAM_GROUP, segments: STREAM_GROUP.segments.filter((s) => s.value !== 'vocational') };
    }
    const present = new Set(
      rows
        .map((r) => STREAM_SEGMENT_BY_DB_VALUE[String(r.stream ?? r.streamLabel ?? '').trim().toUpperCase()])
        .filter(Boolean),
    );
    return { ...STREAM_GROUP, segments: STREAM_GROUP.segments.filter((s) => present.has(s.value)) };
  }, [streamWiseData]);

  const allGroups = useMemo(function () {
    return grade === 12 && streamGroup.segments.length ? [...BASE_GROUPS, streamGroup] : BASE_GROUPS;
  }, [grade, streamGroup]);

  const activeGroupDef = allGroups.find(function (g) { return g.id === selectedGroup; }) ?? allGroups[0];

  const metric: 'passPercentage' | 'performanceIndex' =
    metricMode === 'pi' ? 'performanceIndex' : 'passPercentage';
  const showPass = metricMode !== 'pi';
  const showPI = metricMode !== 'passStats';

  function handleGroupChange(g: ClassGroup) {
    const grp = allGroups.find(function (gr) { return gr.id === g; });
    setSelectedGroup(g);
    setSelectedSegment(grp && grp.segments.length > 0 ? grp.segments[0].value : '');
  }

  function handleLevelChange(l: AnalysisLevel) {
    setAnalysisLevel(l);
    setSelectedRegion(null);
    setSelectedState(null);
    setSelectedDistrict(null);
    setSelectedKv(null);
    // Reset classification if current group is not supported at district level
    if (l === 'DISTRICT' && !DISTRICT_SUPPORTED_GROUPS.includes(selectedGroup)) {
      handleGroupChange('overall');
    }
  }

  function navigateToRegion(name: string) {
    navigate('/result/region/' + encodeURIComponent(name.toUpperCase()) + '?class=' + activeTab + '&year=' + selectedYear);
  }

  function navigateToKv(name: string) {
    const kv = sortedKVs.find(function (k) { return k.name === name; });
    if (kv?.kvCode) {
      const regionCode = encodeURIComponent((kv.region || 'KVS').toUpperCase());
      navigate('/result/region/' + regionCode + '/school/' + kv.kvCode + '?class=' + activeTab + '&year=' + selectedYear);
    }
  }

  // Guard: stream is class-12 only
  React.useEffect(function () {
    if (grade === 10 && selectedGroup === 'stream') handleGroupChange('overall');
  }, [grade]); // eslint-disable-line react-hooks/exhaustive-deps

  const displaySegmentLabel = useMemo(function () {
    if (selectedGroup === 'overall') return 'Overall';
    const seg = activeGroupDef.segments.find(function (s) { return s.value === selectedSegment; });
    return seg ? activeGroupDef.label + ' ” ' + seg.label : activeGroupDef.label;
  }, [selectedGroup, selectedSegment, activeGroupDef]);

  // ── Region-level API ─────────────────────────────────────────────────────
  const regionApiCategory = useMemo(function (): string | null {
    if (selectedGroup === 'overall') return null;
    return selectedGroup; // group ids now match API category params directly
  }, [selectedGroup]);

  // Overall (no category/subcategory) — new dedicated endpoint, always fetch when region level
  const { data: regionOverallData, isFetching: regionOverallFetching } = useGetResultOverallRegionDetailsQuery(
    { grade: grade as 10 | 12, year: selectedYear },
    { skip: analysisLevel !== 'RO' },
  );

  const { data: regionDetailsData, isFetching: regionDetailsFetching } = useGetResultRegionDetailsQuery(
    {
      grade: grade as 10 | 12,
      year: selectedYear,
      category: regionApiCategory!,
      subcategory: selectedSegment,
    },
    { skip: (analysisLevel !== 'RO' && analysisLevel !== 'KV') || !regionApiCategory || !selectedSegment },
  );

  // KV-level endpoints
  const { data: kvOverallData, isFetching: kvOverallFetching } = useGetResultOverallAllKVsQuery(
    { grade: grade as 10 | 12, year: selectedYear },
    { skip: analysisLevel !== 'KV' || !!regionApiCategory },
  );

  const { data: kvCategoryData, isFetching: kvCategoryFetching } = useGetResultAllKVsDetailsQuery(
    { grade: grade as 10 | 12, year: selectedYear, category: regionApiCategory!, subcategory: selectedSegment },
    { skip: analysisLevel !== 'KV' || !regionApiCategory || !selectedSegment },
  );

  // State-level overall data
  const { data: stateOverallData, isFetching: stateOverallFetching } = useGetResultOverallStateDetailsQuery(
    { grade: grade as 10 | 12, year: selectedYear },
    { skip: analysisLevel !== 'STATE' || !!regionApiCategory },
  );

  // State-level category data
  const { data: stateCategoryData, isFetching: stateCategoryFetching } = useGetResultStateDetailsQuery(
    { grade: grade as 10 | 12, year: selectedYear, category: regionApiCategory!, subcategory: selectedSegment },
    { skip: analysisLevel !== 'STATE' || !regionApiCategory || !selectedSegment },
  );

  // District-level overall data
  const { data: districtOverallData, isFetching: districtOverallFetching } = useGetResultOverallDistrictDetailsQuery(
    { grade: grade as 10 | 12, year: selectedYear },
    { skip: analysisLevel !== 'DISTRICT' || !!regionApiCategory },
  );

  // District-level category data (gender / socialcategory / cwsn only)
  const { data: districtCategoryData, isFetching: districtCategoryFetching } = useGetResultDistrictDetailsQuery(
    { grade: grade as 10 | 12, year: selectedYear, category: regionApiCategory!, subcategory: selectedSegment },
    { skip: analysisLevel !== 'DISTRICT' || !regionApiCategory || !selectedSegment || !DISTRICT_SUPPORTED_GROUPS.includes(selectedGroup) },
  );

  /**
   * Any request feeding the current view still in flight.
   *
   * isFetching, not isLoading: isLoading is true only the FIRST time a cache key
   * is seen, so changing Class / Year / Level / Classification / Segment left the
   * previous numbers on screen with nothing to say they were about to change.
   * A skipped query reports isFetching = false, so only the queries that actually
   * run for the current level contribute.
   */
  const isBusy =
    regionOverallFetching || regionDetailsFetching ||
    kvOverallFetching || kvCategoryFetching ||
    stateOverallFetching || stateCategoryFetching ||
    districtOverallFetching || districtCategoryFetching;

  // Map API summary → EntityRow[]
  const activeRegions = useMemo<EntityRow[]>(function () {
    if (regionApiCategory) {
      // New format: data.RO[] — region-level aggregates (excludes KVS OVERALL)
      if (regionDetailsData?.RO && regionDetailsData.RO.length > 0) {
        return regionDetailsData.RO
          .filter(function (item) { return item.roName !== 'KVS OVERALL'; })
          .map(function (item) {
            const name = item.roName.toUpperCase();
            return {
              name,
              passPercentage: item.passPercentage,
              performanceIndex: item.performanceIndex,
              totalRegistered: item.totalRegistered,
              totalAppeared: item.totalAppeared,
              totalPassed: item.totalPassed,
              totalCompartment: item.totalCompartment,
              totalAbsent: item.totalAbsent,
              totalEssentialRepeat: (item as any).totalEssentialRepeat ?? 0,
              nationalRank: null,
              roRank: null,
            };
          });
      }
      // Legacy format: summary[]
      if (regionDetailsData?.summary && regionDetailsData.summary.length > 0) {
        return regionDetailsData.summary.map(function (item) {
          const name = item.region.toUpperCase();
          return {
            name,
            passPercentage: item.passPercentage,
            performanceIndex: item.performanceIndex,
            totalRegistered: item.totalRegistered,
            totalAppeared: item.totalAppeared,
            totalPassed: item.totalPassed,
            totalCompartment: item.totalCompartment,
            totalAbsent: item.totalAbsent,
            totalEssentialRepeat: (item as any).totalEssentialRepeat ?? 0,
            nationalRank: item.nationalRank ?? null,
            roRank: item.roRank ?? null,
          };
        });
      }
      // Legacy format: Schools array
      if (regionDetailsData?.Schools && regionDetailsData.Schools.length > 0) {
        return regionDetailsData.Schools.map(function (school) {
          return {
            name: school.schoolName,
            passPercentage: school.passPercentage,
            performanceIndex: school.performanceIndex,
            totalRegistered: school.totalRegistered,
            totalAppeared: school.totalAppeared,
            totalPassed: school.totalPassed,
            totalCompartment: school.totalCompartment,
            totalAbsent: school.totalAbsent,
            nationalRank: school.nationalRank ?? null,
            roRank: school.roRank ?? null,
            state: school.state,
            district: school.district,
            sector: school.sector,
            kvCode: String(school.kvCode),
          };
        });
      }
    } else {
      // No category filter: use new overall endpoint (data.regions[], excludes KVS OVERALL row)
      if (regionOverallData?.regions && regionOverallData.regions.length > 0) {
        return regionOverallData.regions
          .filter(function (item) { return item.region !== 'KVS OVERALL'; })
          .map(function (item) {
            const name = item.region.toUpperCase();
            return {
              name,
              passPercentage: parseFloat(item.passPercentage),
              performanceIndex: parseFloat(item.performanceIndex),
              totalRegistered: item.totalRegistered,
              totalAppeared: item.totalAppeared,
              totalPassed: item.totalPassed,
              totalCompartment: item.totalCompartment,
              totalAbsent: item.totalAbsent,
              totalEssentialRepeat: (item as any).totalEssentialRepeat ?? 0,
              nationalRank: item.nationalRank ?? null,
              roRank: item.roRank ?? null,
            };
          });
      }
    }
    return [];
  }, [regionApiCategory, regionDetailsData, regionOverallData]);

  // Map active response's overall stats → SegmentStats for stat cards
  const regionNationalStats = useMemo<SegmentStats>(function () {
    if (regionApiCategory) {
      // New format: find KVS OVERALL row in data.RO[]
      if (regionDetailsData?.RO && regionDetailsData.RO.length > 0) {
        const overall = regionDetailsData.RO.find(function (r) { return r.roName === 'KVS OVERALL'; });
        if (overall) {
          return {
            totalStudents: overall.totalAppeared,
            totalRegistered: overall.totalRegistered,
            totalPassed: overall.totalPassed,
            passPercentage: overall.passPercentage,
            performanceIndex: overall.performanceIndex,
            totalCompartment: overall.totalCompartment,
            totalAbsent: overall.totalAbsent,
            totalEssentialRepeat: (overall as any).totalEssentialRepeat ?? 0,
          };
        }
      }
      // Legacy format: overAllSummary
      if (regionDetailsData?.overAllSummary) {
        const s = regionDetailsData.overAllSummary;
        return {
          totalStudents: s.totalAppeared,
          totalRegistered: s.totalRegistered,
          totalPassed: s.totalPassed,
          passPercentage: s.passPercentage,
          performanceIndex: s.performanceIndex,
          totalCompartment: s.totalCompartment,
          totalAbsent: s.totalAbsent,
          totalEssentialRepeat: (s as any).totalEssentialRepeat ?? 0,
        };
      }
      // Legacy format: aggregate from Schools
      if (regionDetailsData?.Schools && regionDetailsData.Schools.length > 0) {
        const schools = regionDetailsData.Schools;
        const totalAppeared = schools.reduce(function (s, r) { return s + r.totalAppeared; }, 0);
        const totalRegistered = schools.reduce(function (s, r) { return s + r.totalRegistered; }, 0);
        const totalPassed = schools.reduce(function (s, r) { return s + r.totalPassed; }, 0);
        const totalCompartment = schools.reduce(function (s, r) { return s + r.totalCompartment; }, 0);
        const totalAbsent = schools.reduce(function (s, r) { return s + r.totalAbsent; }, 0);
        const totalEssentialRepeat = schools.reduce(function (s, r) { return s + ((r as any).totalEssentialRepeat ?? 0); }, 0);
        const passPercentage = totalAppeared > 0 ? +(totalPassed / totalAppeared * 100).toFixed(2) : 0;
        const performanceIndex = +(schools.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / schools.length).toFixed(2);
        return { totalStudents: totalAppeared, totalRegistered, totalPassed, passPercentage, performanceIndex, totalCompartment, totalAbsent, totalEssentialRepeat };
      }
    }
    if (!regionApiCategory && regionOverallData?.regions) {
      // Overall: find the KVS OVERALL entry in the new endpoint's regions array
      const overall = regionOverallData.regions.find(function (r) { return r.region === 'KVS OVERALL'; });
      if (overall) {
        return {
          totalStudents: overall.totalAppeared,
          totalRegistered: overall.totalRegistered,
          totalPassed: overall.totalPassed,
          passPercentage: parseFloat(overall.passPercentage),
          performanceIndex: parseFloat(overall.performanceIndex),
          totalCompartment: overall.totalCompartment,
          totalAbsent: overall.totalAbsent,
          totalEssentialRepeat: (overall as any).totalEssentialRepeat ?? 0,
        };
      }
    }
    return { totalStudents: 0, passPercentage: 0, performanceIndex: 0 };
  }, [regionApiCategory, regionDetailsData, regionOverallData]);

  const comparisonData = null as ComparisonItem[] | null;

  // National stats for KV view
  const kvNationalStats = useMemo<SegmentStats>(function () {
    const kvs = (regionApiCategory ? kvCategoryData?.KVs : kvOverallData?.KVs) ?? [];
    if (kvs.length === 0) return { totalStudents: 0, passPercentage: 0, performanceIndex: 0 };
    const totalAppeared = kvs.reduce(function (s, r) { return s + r.totalAppeared; }, 0);
    const totalRegistered = kvs.reduce(function (s, r) { return s + r.totalRegistered; }, 0);
    const totalPassed = kvs.reduce(function (s, r) { return s + r.totalPassed; }, 0);
    const totalCompartment = kvs.reduce(function (s, r) { return s + r.totalCompartment; }, 0);
    const totalAbsent = kvs.reduce(function (s, r) { return s + r.totalAbsent; }, 0);
    const totalEssentialRepeat = kvs.reduce(function (s, r) { return s + ((r as any).totalEssentialRepeat ?? 0); }, 0);
    const passPercentage = totalAppeared > 0 ? +(totalPassed / totalAppeared * 100).toFixed(2) : 0;
    const performanceIndex = +(kvs.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / kvs.length).toFixed(2);
    return { totalStudents: totalAppeared, totalRegistered, totalPassed, passPercentage, performanceIndex, totalCompartment, totalAbsent, totalEssentialRepeat };
  }, [regionApiCategory, kvOverallData, kvCategoryData]);

  // Sorted snapshots (spread to avoid mutation)
  const sortedRegions = useMemo(function () { return [...activeRegions].sort(function (a, b) { return b[metric] - a[metric]; }); }, [activeRegions, metric]);

  const sortedStates = useMemo<EntityRow[]>(function () {
    const stateSource = (regionApiCategory ? stateCategoryData : stateOverallData);
    if (!stateSource?.States || stateSource.States.length === 0) return [];
    return [...stateSource.States]
      .filter(function (s) { return s.stateName !== 'KVS OVERALL'; })
      .map(function (s) {
        return {
          name: s.stateName,
          passPercentage: s.passPercentage,
          performanceIndex: s.performanceIndex,
          totalRegistered: s.totalRegistered,
          totalAppeared: s.totalAppeared,
          totalPassed: s.totalPassed,
          totalCompartment: s.totalCompartment,
          totalAbsent: s.totalAbsent,
          nationalRank: s.nationalRank,
        };
      })
      .sort(function (a, b) { return b[metric] - a[metric]; });
  }, [regionApiCategory, stateOverallData, stateCategoryData, metric]);

  const stateNationalStats = useMemo<SegmentStats>(function () {
    const stateSource = (regionApiCategory ? stateCategoryData : stateOverallData);
    if (!stateSource?.States || stateSource.States.length === 0) return { totalStudents: 0, passPercentage: 0, performanceIndex: 0 };
    // When category data is active, KVS OVERALL row holds the pre-aggregated national summary
    if (regionApiCategory) {
      const overall = stateSource.States.find(function (s) { return s.stateName === 'KVS OVERALL'; });
      if (overall) {
        return {
          totalStudents: overall.totalAppeared,
          totalRegistered: overall.totalRegistered,
          totalPassed: overall.totalPassed,
          passPercentage: overall.passPercentage,
          performanceIndex: overall.performanceIndex,
          totalCompartment: overall.totalCompartment,
          totalAbsent: overall.totalAbsent,
          totalEssentialRepeat: overall.totalEssentialRepeat ?? 0,
        };
      }
    }
    const states = stateSource.States.filter(function (s) { return s.stateName !== 'KVS OVERALL'; });
    if (states.length === 0) return { totalStudents: 0, passPercentage: 0, performanceIndex: 0 };
    const totalAppeared = states.reduce(function (s, r) { return s + r.totalAppeared; }, 0);
    const totalRegistered = states.reduce(function (s, r) { return s + r.totalRegistered; }, 0);
    const totalPassed = states.reduce(function (s, r) { return s + r.totalPassed; }, 0);
    const totalCompartment = states.reduce(function (s, r) { return s + r.totalCompartment; }, 0);
    const totalAbsent = states.reduce(function (s, r) { return s + r.totalAbsent; }, 0);
    const passPercentage = totalAppeared > 0 ? +(totalPassed / totalAppeared * 100).toFixed(2) : 0;
    const performanceIndex = +(states.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / states.length).toFixed(2);
    return { totalStudents: totalAppeared, totalRegistered, totalPassed, passPercentage, performanceIndex, totalCompartment, totalAbsent };
  }, [regionApiCategory, stateOverallData, stateCategoryData]);

  const sortedDistricts = useMemo<EntityRow[]>(function () {
    const districtSource = (regionApiCategory && DISTRICT_SUPPORTED_GROUPS.includes(selectedGroup) ? districtCategoryData : districtOverallData);
    if (!districtSource?.districts || districtSource.districts.length === 0) return [];
    return [...districtSource.districts]
      .filter(function (d) { return d.districtName !== 'KVS OVERALL'; })
      .map(function (d) {
        return {
          name: d.districtName,
          passPercentage: d.passPercentage,
          performanceIndex: d.performanceIndex,
          totalRegistered: d.totalRegistered,
          totalAppeared: d.totalAppeared,
          totalPassed: d.totalPassed,
          totalCompartment: d.totalCompartment,
          totalAbsent: d.totalAbsent,
          nationalRank: d.nationalRank,
          roRank: d.roRank,
          state: d.stateName,
          region: d.region,
        };
      })
      .sort(function (a, b) { return b[metric] - a[metric]; });
  }, [regionApiCategory, selectedGroup, districtOverallData, districtCategoryData, metric]);

  const districtNationalStats = useMemo<SegmentStats>(function () {
    const districtSource = (regionApiCategory && DISTRICT_SUPPORTED_GROUPS.includes(selectedGroup) ? districtCategoryData : districtOverallData);
    if (!districtSource?.districts || districtSource.districts.length === 0) return { totalStudents: 0, passPercentage: 0, performanceIndex: 0 };
    // KVS OVERALL row holds the pre-aggregated national summary in all cases
    const overall = districtSource.districts.find(function (d) { return d.districtName === 'KVS OVERALL'; });
    if (overall) {
      return {
        totalStudents: overall.totalAppeared,
        totalRegistered: overall.totalRegistered,
        totalPassed: overall.totalPassed,
        passPercentage: overall.passPercentage,
        performanceIndex: overall.performanceIndex,
        totalCompartment: overall.totalCompartment,
        totalAbsent: overall.totalAbsent,
        totalEssentialRepeat: overall.totalEssentialRepeat ?? 0,
      };
    }
    const dists = districtSource.districts.filter(function (d) { return d.districtName !== 'KVS OVERALL'; });
    if (dists.length === 0) return { totalStudents: 0, passPercentage: 0, performanceIndex: 0 };
    const totalAppeared = dists.reduce(function (s, r) { return s + r.totalAppeared; }, 0);
    const totalRegistered = dists.reduce(function (s, r) { return s + r.totalRegistered; }, 0);
    const totalPassed = dists.reduce(function (s, r) { return s + r.totalPassed; }, 0);
    const totalCompartment = dists.reduce(function (s, r) { return s + r.totalCompartment; }, 0);
    const totalAbsent = dists.reduce(function (s, r) { return s + r.totalAbsent; }, 0);
    const passPercentage = totalAppeared > 0 ? +(totalPassed / totalAppeared * 100).toFixed(2) : 0;
    const performanceIndex = +(dists.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / dists.length).toFixed(2);
    return { totalStudents: totalAppeared, totalRegistered, totalPassed, passPercentage, performanceIndex, totalCompartment, totalAbsent };
  }, [regionApiCategory, selectedGroup, districtOverallData, districtCategoryData]);
  const sortedKVs = useMemo<EntityRow[]>(function () {
    // Determine source: new dedicated KV endpoints take priority
    const kvSource = regionApiCategory ? kvCategoryData?.KVs : kvOverallData?.KVs;
    if (kvSource && kvSource.length > 0) {
      return [...kvSource]
        .map(function (kv) {
          return {
            name: kv.schoolName,
            passPercentage: kv.passPercentage,
            performanceIndex: kv.performanceIndex,
            totalRegistered: kv.totalRegistered,
            totalAppeared: kv.totalAppeared,
            totalPassed: kv.totalPassed,
            totalCompartment: kv.totalCompartment,
            totalAbsent: kv.totalAbsent,
            totalEssentialRepeat: kv.totalEssentialRepeat ?? 0,
            nationalRank: kv.nationalRank,
            roRank: kv.roRank,
            region: kv.region,
            state: kv.state,
            district: kv.district,
            sector: kv.sector,
            kvCode: String(kv.kvCode),
          };
        })
        .sort(function (a, b) { return b[metric] - a[metric]; });
    }
    // Legacy fallback: regionDetails Schools array
    if (regionDetailsData?.Schools && regionDetailsData.Schools.length > 0) {
      return [...regionDetailsData.Schools]
        .map(function (school) {
          return {
            name: school.schoolName,
            passPercentage: school.passPercentage,
            performanceIndex: school.performanceIndex,
            totalRegistered: school.totalRegistered,
            totalAppeared: school.totalAppeared,
            totalPassed: school.totalPassed,
            totalCompartment: school.totalCompartment,
            totalAbsent: school.totalAbsent,
            totalEssentialRepeat: (school as any).totalEssentialRepeat ?? 0,
            nationalRank: school.nationalRank ?? null,
            roRank: school.roRank ?? null,
            state: school.state,
            district: school.district,
            sector: school.sector,
            kvCode: String(school.kvCode),
          };
        })
        .sort(function (a, b) { return b[metric] - a[metric]; });
    }
    return [];
  }, [regionApiCategory, kvOverallData, kvCategoryData, regionDetailsData, metric]);

  const selectedRegionRow = activeRegions.find(function (r) { return r.name === selectedRegion; });
  const selectedStateRow = sortedStates.find(function (r) { return r.name === selectedState; });
  const selectedDistrictRow = sortedDistricts.find(function (r) { return r.name === selectedDistrict; });
  const selectedKvRow = sortedKVs.find(function (r) { return r.name === selectedKv; });

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">

      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#F36B21] flex items-center gap-2">
            <BarChart3 className="w-7 h-7" />
            Classification Analysis
          </h1>
          <p className="text-gray-500 text-sm mt-1">CBSE Board Segment-wise performance deep dive</p>
        </div>
        <div className="flex flex-wrap gap-2.5 flex-shrink-0">
          <button
            onClick={function () { navigate('/result/comparison'); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors"
          >
            <GitCompare className="w-4 h-4" />
            Class 10 & 12 Comparison
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={function () { navigate('/result/subject-analysis'); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors"
          >
            <BookOpen className="w-4 h-4" />
            Subject-wise Analysis
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={function () { navigate('/result/yearwise-analysis'); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors"
          >
            <TrendingUp className="w-4 h-4" />
            Year-wise Analysis
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* -- Filter panel --------------------------------------------------------- */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">

        {/* Row 1: Class dropdown + Year dropdown + Level tabs -- all in one line */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-4">
          {/* Class dropdown */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <label className="text-sm font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">Class</label>
            <div className="relative">
              <select
                value={activeTab}
                onChange={function (e) { setActiveTab(e.target.value as GradeTab); }}
                className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21] cursor-pointer"
              >
                {GRADE_TABS.map(function (tab) {
                  return <option key={tab.id} value={tab.id}>{tab.label}</option>;
                })}
              </select>
              <ChevronRight className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90" />
            </div>
          </div>

          {/* Year dropdown */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <label className="text-sm font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">Year</label>
            <div className="relative">
              <select
                value={selectedYear}
                onChange={function (e) { setSelectedYear(Number(e.target.value)); }}
                className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21] cursor-pointer"
              >
                {yearOptions.map(function (y) {
                  return <option key={y} value={y}>{y - 1}-{String(y).slice(-2)}</option>;
                })}
              </select>
              <ChevronRight className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90" />
            </div>
          </div>

          {/* Separator */}
          <div className="h-8 w-px bg-gray-200 flex-shrink-0 hidden sm:block" />

          {/* Level tabs */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">Level</span>
            <div className="flex flex-wrap gap-1.5">
              {ANALYSIS_LEVELS.map(function (l) {
                const Icon = l.icon;
                return (
                  <Chip
                    key={l.id}
                    label={l.label}
                    active={analysisLevel === l.id}
                    onClick={function () { handleLevelChange(l.id); }}
                    icon={<Icon className="w-3.5 h-3.5" />}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Row 2: View toggle */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">View</span>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>

        {/* Row 3: Classification */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">Classification</span>
          <div className="flex flex-wrap gap-2">
            {allGroups.map(function (g) {
              const isDisabled = analysisLevel === 'DISTRICT' && !DISTRICT_SUPPORTED_GROUPS.includes(g.id);
              return (
                <Chip
                  key={g.id}
                  label={g.label}
                  active={selectedGroup === g.id}
                  onClick={function () { handleGroupChange(g.id); }}
                  disabled={isDisabled}
                />
              );
            })}
          </div>
        </div>

        {/* Segment sub-row */}
        {activeGroupDef.segments.length > 0 && (
          <div className="px-6 py-2.5 flex flex-wrap items-center gap-3 bg-gray-50/60">
            <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">Segment</span>
            <div className="flex flex-wrap gap-2">
              {activeGroupDef.segments.map(function (seg) {
                return (
                  <Chip
                    key={seg.value}
                    label={seg.label}
                    active={selectedSegment === seg.value}
                    onClick={function () { setSelectedSegment(seg.value); }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Row 4: Metric */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 whitespace-nowrap">Metric</span>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {METRIC_MODES.map(function (m) {
              return (
                <button
                  key={m.id}
                  onClick={function () { setMetricMode(m.id); }}
                  className={
                    'px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ' +
                    (metricMode === m.id ? 'bg-white text-[#F36B21] shadow-sm' : 'text-gray-500 hover:text-gray-700')
                  }
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active filter summary + breadcrumb  */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap text-sm text-gray-500">
          <span className="font-semibold text-gray-700">Showing:</span>
          <span className="px-2 py-0.5 bg-orange-50 border border-orange-200 rounded-full text-orange-700 font-semibold">
            {activeTab === 'class10' ? 'Class 10' : 'Class 12'}
          </span>
          <span className="px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full text-blue-700 font-semibold">
            {selectedYear - 1}-{String(selectedYear).slice(-2)}
          </span>
          <span className="px-2 py-0.5 bg-purple-50 border border-purple-100 rounded-full text-purple-700 font-semibold capitalize">
            {analysisLevel}
          </span>
          <span className="px-2 py-0.5 bg-green-50 border border-green-100 rounded-full text-green-700 font-semibold">
            {displaySegmentLabel}
          </span>
          <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded-full text-gray-700 font-semibold">
            {METRIC_MODES.find(function (m) { return m.id === metricMode; })?.label}
          </span>
          {/* Feedback at the point of interaction. The full-size loader sits
              further down the page and is often below the fold, so on a tall
              screen a filter change looked like nothing had happened. */}
          {isBusy && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F36B21]" role="status" aria-live="polite">
              <span className="w-3 h-3 rounded-full border-2 border-orange-200 border-t-[#F36B21] animate-spin" />
              Updating…
            </span>
          )}
        </div>
        <LevelBreadcrumb
          region={selectedRegion}
          state={selectedState}
          district={selectedDistrict}
          kv={selectedKv}
          onNavigate={function (l) {
            setAnalysisLevel(l);
            if (l === 'RO') { setSelectedRegion(null); setSelectedState(null); setSelectedDistrict(null); setSelectedKv(null); }
            else if (l === 'STATE') { setSelectedState(null); setSelectedDistrict(null); setSelectedKv(null); }
            else if (l === 'DISTRICT') { setSelectedDistrict(null); setSelectedKv(null); }
          }}
        />
      </div>

      {isBusy && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <KVSLoader size={80} color="#F36B21" />
            <p className="text-sm text-gray-400 mt-4">Loading data…</p>
          </div>
        </div>
      )}

      {analysisLevel === 'RO' && (
        <>
          <div>
            <SectionLabel>{displaySegmentLabel} National Summary</SectionLabel>
            <SummaryCards stats={regionNationalStats} metricMode={metricMode} label={displaySegmentLabel} />
          </div>

          {selectedRegion && selectedRegionRow && (
            <div>
              <SectionLabel>{selectedRegion} Region Summary</SectionLabel>
              <AggSummaryCards row={selectedRegionRow} label={selectedRegion} metricMode={metricMode} />
            </div>
          )}

          {viewMode === 'graphical' && (
            <>
              {comparisonData && comparisonData.length > 0 && !selectedRegion && (
                <div>
                  <SectionLabel>{activeGroupDef.label} Segment Breakdown (National)</SectionLabel>
                  <SegmentComparisonChart
                    data={comparisonData}
                    metricMode={metricMode}
                    groupLabel={activeGroupDef.label}
                  />
                </div>
              )}

              <div>
                <SectionLabel>Region Rankings — {displaySegmentLabel}</SectionLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RankingPanel
                    items={sortedRegions.slice(0, 5)}
                    metric={metric}
                    type="top"
                    title="Top 5 Performing Regions"
                    onItemClick={navigateToRegion}
                  />
                  <RankingPanel
                    items={[...sortedRegions].reverse().slice(0, 5)}
                    metric={metric}
                    type="bottom"
                    title="Bottom 5 Needs Attention"
                    onItemClick={navigateToRegion}
                  />
                </div>
              </div>

              <div>
                <SectionLabel>Regional Deviation Analysis</SectionLabel>
                <div className={'grid grid-cols-1 gap-4 ' + (showPass && showPI ? 'lg:grid-cols-2' : '')}>
                  {showPass && (
                    <DivergingChart
                      data={activeRegions}
                      metric="passPercentage"
                      title="Pass % Regional Deviation"
                      posColor="#10b981"
                      negColor="#f43f5e"
                      onBarClick={navigateToRegion}
                    />
                  )}
                  {showPI && (
                    <DivergingChart
                      data={activeRegions}
                      metric="performanceIndex"
                      title="Performance Index Regional Deviation"
                      posColor="#3b82f6"
                      negColor="#f97316"
                      onBarClick={navigateToRegion}
                    />
                  )}
                </div>
              </div>

              {/* All-regions picker */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-800 mb-3">All Regions</h3>
                <div className="flex flex-wrap gap-2">
                  {sortedRegions.map(function (r) {
                    const avg = activeRegions.reduce(function (s, x) { return s + x[metric]; }, 0) / (activeRegions.length || 1);
                    return (
                      <button
                        key={r.name}
                        onClick={function () { navigateToRegion(r.name); }}
                        className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg border text-sm font-medium transition-colors bg-white text-gray-700 border-gray-200 hover:border-[#F36B21] hover:text-[#F36B21]"
                      >
                        <Network className="w-3.5 h-3.5" />
                        {r.name}
                        <span className={
                          'ml-0.5 text-sm font-bold ' +
                          (r[metric] >= avg ? 'text-green-600' : 'text-red-500')
                        }>
                          {r[metric]}%
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {viewMode === 'tabular' && (
            <div>
              <SectionLabel>Region-wise Detail — {displaySegmentLabel}</SectionLabel>
              <AggTable
                data={activeRegions}
                entityLabel="Region"
                hideRoRank={true}
                onDrillDown={navigateToRegion}
              />
            </div>
          )}
        </>
      )}

      {analysisLevel === 'STATE' && (
        <>
          <div>
            <SectionLabel>
              {selectedState
                ? selectedState + ' — State Summary'
                : selectedRegion
                  ? selectedRegion + ' — States'
                  : 'National State Overview — ' + displaySegmentLabel}
            </SectionLabel>
            {selectedState && selectedStateRow
              ? <AggSummaryCards row={selectedStateRow} label={selectedState} metricMode={metricMode} />
              : <SummaryCards stats={stateNationalStats} metricMode={metricMode} label={displaySegmentLabel} />}
          </div>

          {!selectedRegion && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-orange-700 mr-1">Filter by Region:</span>
              {activeRegions.map(function (r) {
                return (
                  <button
                    key={r.name}
                    onClick={function () { setSelectedRegion(r.name); }}
                    className="px-3 py-1 text-sm font-medium rounded-lg border border-orange-200 bg-white text-orange-700 hover:bg-orange-100 transition-colors"
                  >
                    {r.name}
                  </button>
                );
              })}
            </div>
          )}

          {viewMode === 'graphical' && (
            <>
              <div>
                <SectionLabel>State Rankings — {selectedRegion ?? 'National'}</SectionLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RankingPanel
                    items={sortedStates.slice(0, 5)}
                    metric={metric}
                    type="top"
                    title="Top 5 States"
                    onItemClick={function (name) { setSelectedState(name); }}
                  />
                  <RankingPanel
                    items={[...sortedStates].reverse().slice(0, 5)}
                    metric={metric}
                    type="bottom"
                    title="Bottom 5 States"
                    onItemClick={function (name) { setSelectedState(name); }}
                  />
                </div>
              </div>

              <div>
                <SectionLabel>State Deviation Analysis</SectionLabel>
                <div className={'grid grid-cols-1 gap-4 ' + (showPass && showPI ? 'lg:grid-cols-2' : '')}>
                  {showPass && (
                    <DivergingChart
                      data={sortedStates}
                      metric="passPercentage"
                      title="Pass % State Deviation"
                      posColor="#10b981"
                      negColor="#f43f5e"
                      onBarClick={function (name) { setSelectedState(name); }}
                    />
                  )}
                  {showPI && (
                    <DivergingChart
                      data={sortedStates}
                      metric="performanceIndex"
                      title="PI State Deviation"
                      posColor="#3b82f6"
                      negColor="#f97316"
                      onBarClick={function (name) { setSelectedState(name); }}
                    />
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-800 mb-3">All States</h3>
                <div className="flex flex-wrap gap-2">
                  {sortedStates.map(function (s) {
                    const avg = sortedStates.reduce(function (sum, x) { return sum + x[metric]; }, 0) / (sortedStates.length || 1);
                    return (
                      <button
                        key={s.name}
                        onClick={function () { setSelectedState(selectedState === s.name ? null : s.name); }}
                        className={
                          'flex items-center gap-2 px-3.5 py-1.5 rounded-lg border text-sm font-medium transition-colors ' +
                          (selectedState === s.name
                            ? 'bg-[#F36B21] text-white border-[#F36B21]'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-[#F36B21] hover:text-[#F36B21]')
                        }
                      >
                        <Map className="w-3.5 h-3.5" />
                        {s.name}
                        <span className={
                          'ml-0.5 text-sm font-bold ' +
                          (selectedState === s.name ? 'text-white' : s[metric] >= avg ? 'text-green-600' : 'text-red-500')
                        }>
                          {s[metric]}%
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {viewMode === 'tabular' && (
            <div>
              <SectionLabel>State-wise Detail — {selectedRegion ?? 'National'}</SectionLabel>
              <AggTable
                data={sortedStates}
                entityLabel="State"
                onDrillDown={function (name) { setSelectedState(selectedState === name ? null : name); }}
              />
            </div>
          )}
        </>
      )}

      {analysisLevel === 'DISTRICT' && (
        <>
          <div>
            <SectionLabel>
              {selectedDistrict
                ? selectedDistrict + ' — District Summary'
                : selectedState
                  ? selectedState + ' — Districts'
                  : selectedRegion
                    ? selectedRegion + ' — Districts'
                    : 'National District Overview — ' + displaySegmentLabel}
            </SectionLabel>
            {selectedDistrict && selectedDistrictRow
              ? <AggSummaryCards row={selectedDistrictRow} label={selectedDistrict} metricMode={metricMode} />
              : <SummaryCards stats={districtNationalStats} metricMode={metricMode} label={displaySegmentLabel} />}
          </div>

          {viewMode === 'graphical' && (
            <>
              <div>
                <SectionLabel>
                  District Rankings — {selectedState ?? selectedRegion ?? 'National'}
                </SectionLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RankingPanel
                    items={sortedDistricts.slice(0, 5)}
                    metric={metric}
                    type="top"
                    title="Top 5 Districts"
                    onItemClick={function (name) { setSelectedDistrict(name); }}
                  />
                  <RankingPanel
                    items={[...sortedDistricts].reverse().slice(0, 5)}
                    metric={metric}
                    type="bottom"
                    title="Bottom 5 Districts"
                    onItemClick={function (name) { setSelectedDistrict(name); }}
                  />
                </div>
              </div>

              <div>
                <SectionLabel>District Deviation Analysis</SectionLabel>
                <div className={'grid grid-cols-1 gap-4 ' + (showPass && showPI ? 'lg:grid-cols-2' : '')}>
                  {showPass && (
                    <DivergingChart
                      data={sortedDistricts}
                      metric="passPercentage"
                      title="Pass % — District Deviation"
                      posColor="#10b981"
                      negColor="#f43f5e"
                      onBarClick={function (name) { setSelectedDistrict(name); }}
                    />
                  )}
                  {showPI && (
                    <DivergingChart
                      data={sortedDistricts}
                      metric="performanceIndex"
                      title="PI — District Deviation"
                      posColor="#3b82f6"
                      negColor="#f97316"
                      onBarClick={function (name) { setSelectedDistrict(name); }}
                    />
                  )}
                </div>
              </div>

              <div>
                <SectionLabel>District Bar Chart (Top 20)</SectionLabel>
                <EntityBarChart
                  data={sortedDistricts}
                  metric={metric}
                  title={'Districts — ' + displaySegmentLabel}
                  limit={20}
                  onBarClick={function (name) { setSelectedDistrict(name); }}
                />
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-800 mb-3">All Districts</h3>
                <div className="flex flex-wrap gap-2">
                  {sortedDistricts.map(function (d) {
                    const avg = sortedDistricts.reduce(function (sum, x) { return sum + x[metric]; }, 0) / (sortedDistricts.length || 1);
                    return (
                      <button
                        key={d.name}
                        onClick={function () { setSelectedDistrict(selectedDistrict === d.name ? null : d.name); }}
                        className={
                          'flex items-center gap-2 px-3.5 py-1.5 rounded-lg border text-sm font-medium transition-colors ' +
                          (selectedDistrict === d.name
                            ? 'bg-[#F36B21] text-white border-[#F36B21]'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-[#F36B21] hover:text-[#F36B21]')
                        }
                      >
                        <Layers className="w-3.5 h-3.5" />
                        {d.name}
                        <span className={
                          'ml-0.5 text-sm font-bold ' +
                          (selectedDistrict === d.name ? 'text-white' : d[metric] >= avg ? 'text-green-600' : 'text-red-500')
                        }>
                          {d[metric]}%
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {viewMode === 'tabular' && (
            <div>
              <SectionLabel>
                District-wise Detail — {selectedState ?? selectedRegion ?? 'National'}
              </SectionLabel>
              <AggTable
                data={sortedDistricts}
                entityLabel="District"
                hideRoRank={true}
                hideNationalRank={true}
                onDrillDown={function (name) { setSelectedDistrict(selectedDistrict === name ? null : name); }}
              />
            </div>
          )}
        </>
      )}
      {analysisLevel === 'KV' && (
        <>
          <div>
            <SectionLabel>
              {selectedKv
                ? selectedKv + ' — KV Summary'
                : 'KV Performance — ' + (selectedDistrict ?? selectedState ?? selectedRegion ?? 'National')}
            </SectionLabel>
            {selectedKv && selectedKvRow
              ? <AggSummaryCards row={selectedKvRow} label="This KV" metricMode={metricMode} />
              : <SummaryCards stats={kvNationalStats} metricMode={metricMode} label={displaySegmentLabel} />}
          </div>

          {selectedKv && selectedKvRow && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-4">{selectedKv} — Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {[
                  { label: 'KV Code', value: selectedKvRow.kvCode ?? '', mono: true },
                  { label: 'Region', value: selectedKvRow.region ?? '' },
                  { label: 'State', value: selectedKvRow.state ?? '' },
                  { label: 'District', value: selectedKvRow.district ?? '' },
                  { label: 'Sector', value: selectedKvRow.sector ?? '' },
                  { label: 'Appeared', value: selectedKvRow.totalAppeared.toLocaleString() },
                  { label: 'Passed', value: selectedKvRow.totalPassed.toLocaleString() },
                  { label: 'Compartment', value: selectedKvRow.totalCompartment.toLocaleString() },
                ].map(function (item) {
                  return (
                    <div key={item.label}>
                      <p className="text-gray-500 text-sm font-semibold uppercase tracking-wide">{item.label}</p>
                      <p className={'mt-1 text-gray-800 font-medium ' + ((item as any).mono ? 'font-mono' : '')}>
                        {item.value}
                      </p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={function () {
                    const regionCode = encodeURIComponent((selectedKvRow.region || 'KVS').toUpperCase());
                    navigate('region/' + regionCode + '/school/' + selectedKvRow.kvCode + '?class=' + activeTab + '&year=' + selectedYear);
                  }}
                  className="text-sm font-semibold text-[#F36B21] hover:underline flex items-center gap-1"
                >
                  View full KV drill-down <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {viewMode === 'graphical' && (
            <>
              <div>
                <SectionLabel>
                  KV Rankings ” {selectedDistrict ?? selectedState ?? selectedRegion ?? 'National'}
                </SectionLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RankingPanel
                    items={sortedKVs.slice(0, 5)}
                    metric={metric}
                    type="top"
                    title="Top 5 KVs"
                    onItemClick={navigateToKv}
                  />
                  <RankingPanel
                    items={[...sortedKVs].reverse().slice(0, 5)}
                    metric={metric}
                    type="bottom"
                    title="Bottom 5 KVs Needs Attention"
                    onItemClick={navigateToKv}
                  />
                </div>
              </div>

              <div>
                <SectionLabel>KV Performance Chart (Top 30)</SectionLabel>
                <EntityBarChart
                  data={sortedKVs.slice(0, 30)}
                  metric={metric}
                  title={'KV Rankings ' + displaySegmentLabel}
                  limit={30}
                  onBarClick={navigateToKv}
                />
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-800 mb-4">
                  All KVs ” {selectedDistrict ?? selectedState ?? selectedRegion ?? 'National'}
                </h3>
                <div className="max-h-72 overflow-y-auto flex flex-col gap-1 pr-1">
                  {sortedKVs.map(function (s) {
                    return (
                      <button
                        key={String(s.kvCode)}
                        onClick={function () { navigateToKv(s.name); }}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-lg border text-sm text-left transition-colors border-gray-200 hover:border-[#F36B21] hover:bg-orange-50"
                      >
                        <School className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="flex-1 text-gray-700 font-medium truncate">{s.name}</span>
                        <span className="text-sm text-gray-400 flex-shrink-0">{s.district ?? s.state}</span>
                        <span className={
                          'text-xs font-bold flex-shrink-0 ' +
                          (s.passPercentage >= 90 ? 'text-green-600' : s.passPercentage >= 75 ? 'text-yellow-600' : 'text-red-500')
                        }>
                          {s.passPercentage}%
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {viewMode === 'tabular' && (
            <div>
              <SectionLabel>
                KV-wise Performance ” {selectedDistrict ?? selectedState ?? selectedRegion ?? 'National'}
              </SectionLabel>
              <KVTable
                data={sortedKVs}
                onRowClick={function (row) { navigateToKv(row.name); }}
              />
            </div>
          )}
        </>
      )}

    </div>
  );
}